import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import {
  AlertTriangle,
  CalendarClock,
  CheckCheck,
  Search,
  SearchX,
  Send,
  Users,
} from "lucide-react";
import { isValidYmd } from "@contractorhub/shared";
import { apiFetch, errorMessage } from "../../../lib/api";
import { adminChaseQuery } from "../../../lib/queries";
import {
  Alert,
  Avatar,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Pagination,
  StatCard,
  StatusBadge,
  type Column,
} from "../../../components/ui";
import { useToast } from "../../../components/toast";

const searchSchema = z.object({
  from: z.string().refine(isValidYmd).optional(),
  to: z.string().refine(isValidYmd).optional(),
  contractorId: z.string().optional(),
  clientId: z.string().optional(),
  projectId: z.string().optional(),
});

export const Route = createFileRoute("/_authed/_staff/chase")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(adminChaseQuery(deps)),
  component: ChasePage,
});

const PAGE_SIZE = 25;

function ChasePage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminChaseQuery(search));
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  /**
   * Run the monthly nudge now, rather than waiting for the 25th (R-NT-1).
   *
   * Behind a confirmation because it is the one control on this screen that
   * leaves the building: every other button here filters a list, this one puts
   * mail in front of people. Nothing can unsend it, so the only place to be
   * careful is before.
   *
   * It nudges **everyone** outstanding — there is no per-contractor send — and
   * it is idempotent per contractor per month, so a second press reaches nobody
   * who was already told. That is what `skipped` counts, and reporting it is
   * what stops a no-op looking like a failure.
   */
  const sendReminders = useMutation({
    mutationFn: () => apiFetch("POST /api/manage/reminders/run"),
    onSuccess: async ({ sent, skipped }) => {
      setConfirmSend(false);
      setSendError(null);
      await queryClient.invalidateQueries({ queryKey: ["admin"] });

      if (sent === 0 && skipped === 0) {
        toast("Nobody is behind — no reminders sent");
        return;
      }
      if (sent === 0) {
        toast(
          `No reminders sent — all ${skipped} were already nudged this month`
        );
        return;
      }
      toast(
        skipped > 0
          ? `${sent} reminder${sent === 1 ? "" : "s"} sent · ${skipped} already nudged this month`
          : `${sent} reminder${sent === 1 ? "" : "s"} sent`,
        { tone: "success" }
      );
    },
    onError: (err) => setSendError(errorMessage(err)),
  });

  const patch = (next: Partial<typeof search>) =>
    void navigate({ to: "/chase", search: { ...search, ...next } });

  /*
   * Client-side text search and paging.
   *
   * `GET /api/manage/chase` returns the whole backlog in one response, and the
   * page rendered all of it — with only From/To to narrow it, even though the
   * route's search schema already accepts contractorId/clientId/projectId and
   * offered no control for any of them. A free-text box over the loaded rows is
   * the narrowing an admin actually needs to chase one person; server-side
   * paging on that endpoint remains an open API gap.
   */
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return data.rows;
    return data.rows.filter((row) =>
      [row.contractorName, row.contractorEmail, row.clientName, row.projectName].some(
        (field) => field.toLowerCase().includes(needle)
      )
    );
  }, [data.rows, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const columns: Column<(typeof data.rows)[number]>[] = [
    {
      key: "contractor",
      header: "Contractor",
      cell: (row) => (
        <div className="flex items-center gap-3">
          <Avatar name={row.contractorName} />
          <div className="min-w-0">
            <div className="truncate font-medium text-text">{row.contractorName}</div>
            <div className="truncate text-micro text-subtle">{row.contractorEmail}</div>
            <div className="truncate text-xs text-subtle lg:hidden">
              {row.clientName} · {row.projectName}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "client",
      header: "Client",
      priority: "lg",
      cell: (row) => <span className="text-muted">{row.clientName}</span>,
    },
    {
      key: "project",
      header: "Project",
      priority: "lg",
      cell: (row) => <span className="text-muted">{row.projectName}</span>,
    },
    {
      key: "week",
      header: "Week",
      priority: "sm",
      cell: (row) => <span className="whitespace-nowrap text-muted">{row.weekLabel}</span>,
    },
    {
      key: "state",
      header: "State",
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {/* `StatusBadge` rather than a bespoke pill: the hand-rolled one here
              dropped the border every other badge in the app carries, so these
              two read flatter than everything around them — and it duplicated
              styles that already existed for "Overdue" and "Due". */}
          <StatusBadge status={row.state === "overdue" ? "Overdue" : "Due"} />
          {/* Started but not sent is a different conversation from nothing at
              all — say which. */}
          {row.hasDraft && (
            <span className="whitespace-nowrap text-micro text-subtle">
              draft · {row.draftHours?.toFixed(1)}h
            </span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (row) => (
        <Link
          to="/timesheets"
          search={{
            status: "ALL",
            contractorId: row.contractorId,
            weekStart: row.weekStart,
          }}
          className="whitespace-nowrap rounded-md px-2 py-1 text-xs text-muted hover:bg-surface-hover hover:text-text"
        >
          View week
        </Link>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Not submitted"
        description="Weeks a contractor was engaged for but hasn't submitted. Defaults to the last completed week — the current one isn't due yet."
        actions={
          <Button
            variant="secondary"
            /*
             * Deliberately not disabled on an empty list.
             *
             * This page defaults to the *last completed week*; the reminder is
             * about every completed week of the current month. So the list can
             * be empty while the month still holds unfiled weeks, and gating the
             * button on `counts.contractors` would have greyed out a send that
             * had people to reach. The filters on this screen do not narrow the
             * reminder either -- it is a monthly job with its own range, not an
             * action on the rows in view.
             */
            disabled={sendReminders.isPending}
            onClick={() => {
              setSendError(null);
              setConfirmSend(true);
            }}
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            Send reminders
          </Button>
        }
      />

      {sendError && (
        <Alert tone="error" className="mb-6">
          {sendError}
        </Alert>
      )}

      <Modal
        open={confirmSend}
        onClose={() => setConfirmSend(false)}
        title="Send reminders now?"
        description="Every contractor with an unsubmitted week this month gets one email listing those weeks. Anyone already nudged this month is skipped, and nothing can be unsent."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmSend(false)}>
              Cancel
            </Button>
            <Button
              loading={sendReminders.isPending}
              onClick={() => sendReminders.mutate()}
            >
              Send reminders
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted">
          This is the same nudge the 25th sends on its own, and running it by hand does
          not change when the scheduled one goes out. It covers the whole month, so it
          can reach more people than the list on this page -- which shows a single week
          unless you widen the dates -- and it ignores the filters above.
        </p>
      </Modal>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          label="Overdue"
          value={data.counts.overdue}
          icon={AlertTriangle}
          tone="danger"
        />
        <StatCard label="Due" value={data.counts.due} icon={CalendarClock} tone="warning" />
        <StatCard label="Contractors" value={data.counts.contractors} icon={Users} />
      </div>

      <Card className="mb-6 p-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Search" className="lg:col-span-2">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={query}
                placeholder="Contractor, client or project"
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
          </Field>
          <Field label="From">
            <Input
              type="date"
              value={search.from ?? ""}
              onChange={(e) => patch({ from: e.target.value || undefined })}
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={search.to ?? ""}
              onChange={(e) => patch({ to: e.target.value || undefined })}
            />
          </Field>
        </div>
        {(search.from || search.to) && (
          <div className="mt-3 flex justify-end border-t border-line pt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => patch({ from: undefined, to: undefined })}
            >
              Clear dates
            </Button>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          rows={visible}
          // Per period: both halves of a split week can be owed at once (TIME-1).
          getRowKey={(row) => `${row.contractorId}:${row.projectId}:${row.periodStart}`}
          caption="Weeks not yet submitted"
          stickyHeader
          minWidth="min-w-[720px]"
          empty={
            query ? (
              <EmptyState
                icon={SearchX}
                title="No matches"
                description={`Nothing outstanding matches “${query}”.`}
              />
            ) : (
              // An all-clear is good news, and rendering it in the same flat grey
              // as "your filter matched nothing" told the reader nothing about
              // which of the two they were looking at.
              <EmptyState
                tone="positive"
                icon={CheckCheck}
                title="Everyone is up to date"
                description="No engaged week in this range is missing a timesheet."
              />
            )
          }
        />
        <Pagination
          page={safePage}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onPageChange={setPage}
        />
      </Card>
    </div>
  );
}
